<template>
  <span
    class="turn-pill"
    :class="`turn-pill--${variant}`"
    :role="announce ? 'status' : null"
  >
    <span class="turn-pill__dot" aria-hidden="true" />
    <span class="turn-pill__label">{{ label }}</span>
    <!-- "There's more to read here" affordance. Decorative only — the pill's own
         label already carries the meaning, and the tap target that owns this pill
         (the row anchor, or DecisionChip's button) carries the accessible name. -->
    <f7-icon
      v-if="chevron"
      f7="chevron_right"
      size="13"
      class="turn-pill__chevron"
      aria-hidden="true"
    ></f7-icon>
  </span>
</template>

<script setup>
/**
 * TurnPill — the always-visible "whose court is this in" chip (UX spec principle
 * 3: whose turn is always visible, in plain Arabic). Calm background chips in the
 * feature's semantic hues, never a saturated alert. Consequence is in the TEXT,
 * so meaning never depends on colour alone (color-blind safe).
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { pillLabelKey } from '@/constants/collectionIssue.js';

const props = defineProps({
  // 'member'   → "قرارك مطلوب"  (amber, it's your turn)
  // 'resolved' → done            (green)
  // 'handled'  → "being handled" (calm brand — mid-issue, leader's court)
  // 'oos'      → "out of stock"  (muted gray — terminal awareness)
  variant: {
    type: String,
    default: 'member',
    validator: (v) => ['member', 'resolved', 'handled', 'oos'].includes(v),
  },
  // role="status" makes the pill a live region — right for a standalone display
  // pill, but wrong when the pill is nested inside an interactive control that
  // already carries its own accessible name (DecisionChip's tappable button).
  // Pass :announce="false" there to drop the nested live region.
  announce: {
    type: Boolean,
    default: true,
  },
  // Append a decorative chevron so the pill reads as "opens something". Set it
  // wherever the pill's surface actually navigates — since interaction-v2 a
  // being-handled item can open its issue read-only, and without this the calm
  // «شغالين عليها» pill reads as terminal information the member can't act on.
  chevron: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n({ useScope: 'global' });

const label = computed(() => t(pillLabelKey(props.variant)));
</script>

<style scoped>
.turn-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: var(--poolit-radius-full, 999px);
  padding: 4px 11px;
  font-size: var(--poolit-font-size-small, 0.78rem);
  font-weight: var(--poolit-font-weight-bold, 700);
  line-height: 1.4;
  white-space: nowrap;
}

.turn-pill__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}

/* De-emphasised — an affordance, not a second focal point. The 6px flex `gap`
   above already separates it from the label; the negative end margin claws back
   part of the pill's 11px end padding so the glyph doesn't push the pill wide.
   It sets no colour of its own: f7-icon renders a font glyph in an <i>, which
   inherits the variant's `color`, so the chevron can never re-tint the pill
   (being-handled stays the leader's calm brand, never amber). */
.turn-pill__chevron {
  flex-shrink: 0;
  margin-inline-start: 1px;
  margin-inline-end: -3px;
  opacity: 0.75;
}

.turn-pill--member {
  color: var(--ci-await-fg, #b45309);
  background: var(--ci-await-bg, #fbefd4);
}

.turn-pill--resolved {
  color: var(--ci-ok-fg, #16964f);
  background: var(--ci-ok-bg, #e1f3e8);
}

/* Being handled — a calm brand tint: "Poolit is on it", never an alert. */
.turn-pill--handled {
  color: var(--ci-brand-fg, #6621e2);
  background: var(--ci-brand-bg, #f0eaff);
}

/* Out of stock — a muted, de-emphasised gray: terminal awareness, not alarm. */
.turn-pill--oos {
  color: var(--ci-muted-fg, #374151);
  background: var(--ci-muted-bg, #eceef1);
}
</style>

<!-- Non-scoped ON PURPOSE (CLAUDE.md): the global-pseudo-class compound form
     (global ancestor + scoped descendant) is silently miscompiled and would
     mirror the ENTIRE page. Arabic is the primary locale, so "forward" points
     left — mirror the chevron rather than swapping the glyph. -->
<style>
[dir='rtl'] .turn-pill__chevron {
  transform: scaleX(-1);
}
</style>
